import Widget from "./base.js";
import { postSubscribeUnlock } from 'tpm-lib/post-unlock.js';
import { Icon } from '../lib/fa-icons.js';

// Newsletter signup form with a membership plan-picker overlay, rendered in
// the article paywall (template-parts/paywall.php) for logged-out readers
// via a server-side placeholder (data-widget=NewsletterSignupFormWithOverlay).
//
// Flow: email entry -> "Subscribe" opens the "Choose Your Plan" overlay
// (Prime / Ad-Free / Basic, annual-monthly toggle). Basic closes the overlay
// and POSTs {email, addToMultipleLists, nextPath} to doesspress/email-list,
// then swaps the form for confirmation/error states; paid plans link
// straight to accounts.<host>/membership checkout.
//
// Self-contained: takes `category` and `categoryName` props, does its own
// GET to doesspress/email-list, and resolves the target list via
// categoryToList (slug matching tolerates legacy Brevo underscores vs
// beehiiv hyphens). Also picks up a ?addToMultipleLists= param on mount for
// the post-signin email-prefs flow (POST, then page refresh). Optional
// onConfirmation prop lets a parent react to a completed signup. Styles:
// webpack/styles/site/newsletter-signup-form-with-overlay.scss.

// Normalize slugs for comparison: the legacy Brevo lists use underscores
// (the_backchannel) while editorial-newsletter rows use hyphens
// (the-backchannel). Match either convention so the widget works pre- and
// post-beehiiv-switchover.
const slugMatch = (a, b) => (a ?? "").replace(/[-_]/g, "-") === (b ?? "").replace(/[-_]/g, "-");

// Basic email sanity check — local part, @, domain, TLD, no whitespace —
// enough to gate the subscribe button without rejecting valid addresses.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const OVERLAY_OPTIONS = {
	year: {
		prime: {
			name: "Prime Annual",
			price: "$70/yr",
			link: `https://accounts.${document.location.hostname}/membership?plan=prime_year`
		},
		adfree: {
			name: "Ad Free Annual",
			price: "$140/yr",
			link: `https://accounts.${document.location.hostname}/membership?plan=adfree_year`
		},
	},
	month: {
		prime: {
			name: "Prime Monthly",
			price: "$6.99/mo",
			link: `https://accounts.${document.location.hostname}/membership?plan=prime_month`
		},
		adfree: {
			name: "Ad Free Monthly",
			price: "$13.99/mo",
			link: `https://accounts.${document.location.hostname}/membership?plan=adfree_month`
		}
	},
}

export class NewsletterSignupFormWithOverlay extends Widget {
	componentWillMount() {
		this.state.overlayVisible = false;
		this.state.emailValid = false;
		this.state.planType = 'prime'; //drives sm view
		this.state.interval = true; // false = annual, true = monthly, also drives sm view

		this.handleEmail = this.handleEmail.bind(this);
		this.validateEmailInput = this.validateEmailInput.bind(this);
		this.toggleOverlay = this.toggleOverlay.bind(this);
		this.submit = this.submit.bind(this);
		this.submitWithNoLists = this.submit.bind(this);
		this.toggleInterval = this.toggleInterval.bind(this);
		this.selectPlan = this.selectPlan.bind(this);
		
		this.getData();
	}

	componentDidMount() {
		const urlParams = new URLSearchParams(window.location.search);
		const addToMultipleLists = urlParams.get("addToMultipleLists");
		if (addToMultipleLists) {
			this.postData({
				addToMultipleLists,
				nextPath: encodeURIComponent(window.location.pathname),
			});
		}
	}

	componentDidUpdate(){
	}

	getData() {
		this.request("GET");
	}

	postData(opts) {
		this.request("POST", opts);
	}

	request(method, data = {}) {
		const self = this;

		let params = `?`;
		Object.keys(data).forEach((k) => {
			params += `${k}=${data[k]}&`;
		});

		fetch(
			`https://accounts.${document.location.hostname}/doesspress/email-list${params}`,
			{
				method: `${method}`,
				credentials: "include",
			}
		)
			.then((r) => {
				return r.json();
			})
			.then((r) => {
				if (method === "GET") {
					self.setState({
						emailLists: r.bindings.emailLists,
						ident: r.bindings.ident,
					});
				} else {
					if (r.errors) {
						self.setState({ error: r.errors[0] });
					}
					if (r.confirmation) {
						// Post-subscribe unlock: shared transition (in-place content
						// swap when a token was minted; reload fallback otherwise).
						postSubscribeUnlock(r.confirmation, data);
						self.setState({ confirmation: r.confirmation });
						
						//hide paywall elements that surround the newsletter signup form
						document.querySelectorAll('.NewsletterPaywall__Signin').forEach(el => el.style.display = 'none');
						document.querySelectorAll('.NewsletterPaywall__Title').forEach(el => el.style.display = 'none');
						document.querySelectorAll('.NewsletterPaywall__Title + p').forEach(el => el.style.display = 'none');

						// opt-in hook for parents (e.g. EmailWall treats a
						// subscription as dismissal)
						if (typeof self.props.onConfirmation === "function")
							self.props.onConfirmation(r.confirmation);

					}
				}
			});
	}

	toggleInterval(){
		this.setState({ interval: !this.state.interval });
	}

	toggleOverlay(){
		// don't open the plan overlay until the email input is valid
		if (!this.state.overlayVisible && !this.state.emailValid) return;
		window.body.classList.toggle('modal-overlay-open');
		this.setState({ overlayVisible: !this.state.overlayVisible });
	}

	selectPlan(name, interval) {
		this.setState({ planType: name, interval: interval });
	}

	validateEmailInput(e) {
		this.setState({ emailValid: EMAIL_PATTERN.test(e.target.value) });
	}

	emailValueFromInput() {
		const root = this.refs.base;
		return root.querySelector("#newEmail").value;	
	}

	handleEmail(e = null) {
		if (e) e.preventDefault();

		const root = this.refs.base;
		const checkedInputs = root.querySelectorAll(
			".js_newsletter-checkbox:checked"
		);
		if (checkedInputs.length === 0) {
			this.setState({
				error: { message: "Please select a newsletter to subscribe to" },
			});
			return;
		} else {
			this.setState({
				error: null,
			});
		}

		const listIds = Array.from(checkedInputs).map(
			() => root.querySelector('input[name="listId"]').value
		);

		const opts = {
			addToMultipleLists: listIds.join(","),
			email: this.emailValueFromInput(),
			nextPath: encodeURIComponent(window.location.pathname),
		};

		this.postData(opts);
	}

	submit() {
		this.handleEmail();
	}

	submitWithNoLists(){

		const root = this.refs.base;
		const checkedInputs = root.querySelectorAll(
			".js_newsletter-checkbox:checked"
		);
		checkedInputs.forEach((input) => {
			input.checked = false;
		});
		this.handleEmail();

	}


	categoryToList(category) {
		const lists = this.state.emailLists;

		if (category === "the-weekender") {
			return lists.find((list) => slugMatch(list.slug, "the_weekender"));
		}

		if (category === "the-franchise") {
			return lists.find((list) => slugMatch(list.slug, "the_franchise"));
		}

		if (category === "the-brief") {
			return lists.find((list) => slugMatch(list.slug, "the_brief"));
		}

		if (category === "the-backchannel") {
			return lists.find((list) => slugMatch(list.slug, "the_backchannel"));
		}

		if (category === "morning-memo") {
			return lists.find((list) => slugMatch(list.slug, "morning_memo"));
		}

		if (category === "where-things-stand") {
			return lists.find((list) => slugMatch(list.slug, "where_things_stand"));
		}
		return null;
	}

	categoryNameOneLine(){
		return this.props.categoryName.replace(/ /g, "\u00a0");
	}

	buttonClasses(){
		if (this.props.cssclasses) {
			if (this.props.cssclasses.includes('--dark')) return 'Button --beige-to-black';
			if (this.props.cssclasses.includes('--red')) return 'Button --red-to-transparent';
		}
		return 'Button --red-border-black';
	}

	renderForm(list) {

		const confirmAcctCreation = this.state.confirmation;	

		return (
			<div>
				{this.state.overlayVisible ? this.renderOverlay() : null}
				{confirmAcctCreation && (
					<div className="NewsletterSignupFormWithOverlay__Confirmation">
						<div className="NewsletterSignupFormWithOverlay__ConfirmationTitle"><b>Account Created!</b></div>
						<p className="NewsletterSignupFormWithOverlay__ConfirmationText">Check your email for a link to confirm your account.</p>
						<p className="NewsletterSignupFormWithOverlay__ConfirmationText --sm">
							Don’t see an email? Please check your spam folder or{" "}
							<a href="https://talkingpointsmemo.com/contact">
								contact&nbsp;us
							</a>{" "}
							for assistance.
						</p>
					</div>
				)}
				{!confirmAcctCreation && (
					<div className="NewsletterSignupFormWithOverlay__Form">
						<input type="hidden" name="listId" value={list.id} />
						<input
							className=" js_newsletter-checkbox"
							id={`id_${list.id}`}
							name="addToList"
							type="checkbox"
							defaultChecked={true}
							style={{ display: "none" }}
						/>
						<div className="NewsletterSignupFormWithOverlay__Flex">
							<label
								htmlFor={`id_${list.id}`}
								style={{
									position: "absolute",
									width: "1px",
									height: "1px",
									padding: 0,
									margin: "-1px",
									overflow: "hidden",
									clip: "rect(0,0,0,0)",
									border: 0,
								}}
							>
								Email Address
							</label>
							<input
								type="email"
								id="newEmail"
								required
								placeholder="Email Address"
								onInput={this.validateEmailInput}
							/>
							<div
								className={`NewsletterSignupFormWithOverlay__Button --min-w ${this.buttonClasses()}${this.state.emailValid ? "" : " --disabled"}`}
								aria-disabled={this.state.emailValid ? "false" : "true"}
								onClick={this.toggleOverlay}
							>
								Create Account
							</div>
						</div>
					</div>
				)}
			</div>
		);
	}

	renderOverlay() {
		const confirmAcctCreation = this.state.confirmation;

		const plans = this.state.interval ?  OVERLAY_OPTIONS.month : OVERLAY_OPTIONS.year;

		return(
			<section className="NewsletterSignupFormWithOverlay__Overlay">
				
				<div className="NewsletterSignupFormWithOverlay__OverlayHeader">
					<div className="NewsletterSignupFormWithOverlay__OverlayHeaderInner">
						<img className="NewsletterSignupFormWithOverlay__OverlayLogo" src={`https://${window.location.hostname}/wp-content/themes/tpm25/assets/images/logo/TPM-logo.svg?v=123`} alt="TPM Logo" />
						{confirmAcctCreation && (
							<div className="NewsletterSignupFormWithOverlay__OverlayClose" onClick={this.toggleOverlay}>
								<Icon name="xmark" style="light" />
							</div>
						)}
						{! confirmAcctCreation && (
							<div className="NewsletterSignupFormWithOverlay__OverlayClose --text" onClick={this.submitWithNoLists}>
								Continue Without Plan
							</div>
						)}
					</div>
				</div>

				{confirmAcctCreation && (
					<div className="NewsletterSignupFormWithOverlay__Confirmation">
						<div className="NewsletterSignupFormWithOverlay__ConfirmationTitle"><b>Account Created!</b></div>
						<p className="NewsletterSignupFormWithOverlay__ConfirmationText">Check your email for a link to confirm your account.</p>
						<p className="NewsletterSignupFormWithOverlay__ConfirmationText --sm">
							Don’t see an email? Please check your spam folder or{" "}
							<a href="https://talkingpointsmemo.com/contact">
								contact&nbsp;us
							</a>{" "}
							for assistance.
						</p>
					</div>
				)}
				{this.state.error && (
					<p
						style={{
							background: "#ffefb0",
							padding: "10px",
						}}
					>
						{this.state.error.message}
					</p>
				)}

				{!confirmAcctCreation && (
					<div>
						<div className="NewsletterSignupFormWithOverlay__OverlayTitle">Choose Your Plan</div>
						
						<div className="NewsletterSignupFormWithOverlay__OverlaySwitch" onClick={this.toggleInterval}>
							<label>Annual Plans</label>
							<div className={`NewsletterSignupFormWithOverlay__OverlaySwitchSlider ${this.state.interval ? "--right" : ""}`}></div>
							<label>Monthly Plans</label>
						</div>
						
						<div className="NewsletterSignupFormWithOverlay__OverlayOptions --lg">
							<div className="NewsletterSignupFormWithOverlay__OverlayOption --red-border">
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionTag">
									Most Popular
								</div>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionName">
									{plans.prime.name}
								</div>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionPrice">
									{plans.prime.price}
								</div>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionDesc">
									Members-only edition of<br/>{this.categoryNameOneLine()}
								</div>
								<a className="NewsletterSignupFormWithOverlay__OverlayOptionButton Button --red" href={plans.prime.link + '&email=' + this.emailValueFromInput()}>
									Select
								</a>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionPerks">
									<p>Also Includes:</p>
									<ul className="NewsletterSignupFormWithOverlay__OverlayOptionPerksList">
										<li>Full TPM site access</li>
										<li>Fewer ads</li>
										<li>The Hive Member forum</li>
										<li>Members-only newsletters</li>
										<li>RSS feeds</li>
										<li>Commenting</li>
									</ul>
								</div>
							</div>

							<div className="NewsletterSignupFormWithOverlay__OverlayOption">
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionName">
									{plans.adfree.name}
								</div>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionPrice">
									{plans.adfree.price}
								</div>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionDesc">
									Members-only edition of<br/>{this.categoryNameOneLine()}
								</div>
								<a className="NewsletterSignupFormWithOverlay__OverlayOptionButton Button --red" href={plans.adfree.link + '&email=' + this.emailValueFromInput()}>
									Select
								</a>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionPerks">
									<p>Also Includes:</p>
									<ul className="NewsletterSignupFormWithOverlay__OverlayOptionPerksList">
										<li>Full TPM site access</li>
										<li>NO ADS (email or site)</li>
										<li>The Hive Member forum</li>
										<li>Members-only newsletters</li>
										<li>RSS feeds</li>
										<li>Commenting</li>
									</ul>
								</div>
							</div>
			
							<div className="NewsletterSignupFormWithOverlay__OverlayOption">
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionName">
									Basic
								</div>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionPrice">
									Free
								</div>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionDesc">
									Free edition of<br/>{this.categoryNameOneLine()}
								</div>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionButton Button --red" onClick={this.submit}>
									Subscribe
								</div>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionPerks">
									<p>Also Includes:</p>
									<ul className="NewsletterSignupFormWithOverlay__OverlayOptionPerksList">
										<li>Access to non-members articles</li>
									</ul>
								</div>
							</div>
						</div>

						<div className="NewsletterSignupFormWithOverlay__OverlayOptions --sm">
							<div className="NewsletterSignupFormWithOverlay__OverlayPlans">
								{Object.entries(plans).map(([planId, plan]) => (
									<label key={planId}>
										<input type="radio" name="membershipPlan" value={planId} checked={this.state.planType === planId} onClick={()=>{this.selectPlan(planId, this.state.interval)}}/>
										{plan.name} <small>{plan.price}</small>
									</label>
								))}
								<label key={`basic`}>
									<input type="radio" name="membershipPlan" value={`basic`} onClick={()=>{this.selectPlan('basic', this.state.interval)}}/>
									Basic <small>Free</small>
								</label>
							</div>

							<div className={`NewsletterSignupFormWithOverlay__OverlayOptionPerks ${this.state.planType === 'prime' ? '--visible': '--hidden'}`}>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionDesc">Members-only edition of {this.categoryNameOneLine()}</div>
								<p>Also Includes:</p>
								<ul className="NewsletterSignupFormWithOverlay__OverlayOptionPerksList">
									<li>Full TPM site access</li>
									<li>Fewer Ads</li>
									<li>The Hive Member forum</li>
									<li>Members-only newsletters</li>
									<li>RSS feeds</li>
									<li>Commenting</li>
								</ul>
							</div>

							<div className={`NewsletterSignupFormWithOverlay__OverlayOptionPerks ${this.state.planType === 'adfree' ? '--visible': '--hidden'}`}>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionDesc">Members-only edition of {this.categoryNameOneLine()}</div>
								<p>Also Includes:</p>
								<ul className="NewsletterSignupFormWithOverlay__OverlayOptionPerksList">
									<li>Full TPM site access</li>
									<li>NO ADS (email or site)</li>
									<li>The Hive Member forum</li>
									<li>Members-only newsletters</li>
									<li>RSS feeds</li>
									<li>Commenting</li>
								</ul>
							</div>

							<div className={`NewsletterSignupFormWithOverlay__OverlayOptionPerks ${this.state.planType === 'basic' ? '--visible': '--hidden'}`}>
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionDesc">Free edition of {this.categoryNameOneLine()}</div>
								<p>Also Includes:</p>
								<ul className="NewsletterSignupFormWithOverlay__OverlayOptionPerksList">
									<li>Access to non-members articles</li>
								</ul>
							</div>
			
							{this.state.planType !== 'basic' && (
								<a className="NewsletterSignupFormWithOverlay__OverlayOptionButton Button --red" href={`${this.state.planType === 'prime' ? plans.prime.link : plans.adfree.link}&email=${this.emailValueFromInput()}`}>
									Select
								</a>
							)}

							{this.state.planType === 'basic' && (
								<div className="NewsletterSignupFormWithOverlay__OverlayOptionButton Button --red" onClick={this.submit}>
									Subscribe
								</div>
							)}

						</div>
					</div>
				)}
			</section>
		)
	}

	renderContent() {

		if (this.state.error && !this.state.emailLists) return null;
		if (!this.state.emailLists) return null;
		const list = this.categoryToList(this.props.category);
		if (!list) return null;

		return this.renderForm(list);
	}
}